import posthog from "posthog-js";

let initialized = false;

type PostHogEventProperties = Record<
  string,
  string | number | boolean | null | undefined
>;

export function initPostHog(): void {
  if (initialized || typeof window === "undefined") return;

  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!key) return;

  posthog.init(key, {
    api_host: "/ingest",
    ui_host: "https://us.posthog.com",
    capture_pageview: false,
    capture_pageleave: true,
    autocapture: true,
    persistence: "localStorage+cookie",

    // ───── SESSION REPLAY — TCPA/GDPR-aware masking ─────
    // Replays are critical for understanding *why* users drop off in
    // the simulation wizard, but they cannot leak PII.
    //
    // Default rule: mask all <input> values, mask password/email/tel,
    // and respect any element with the .ph-no-capture class or
    // [data-ph-no-capture] / [data-private] attribute. Audio capture
    // and cross-origin iframes (Stripe) stay off — both for compliance
    // and because Stripe iframes are sandboxed anyway.
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: false,
      maskInputOptions: {
        password: true,
        email: true,
        tel: true,
        // Plain text inputs (name, family name) — we still mask anything
        // explicitly tagged with [data-private] via the selector below.
      },
      maskTextSelector: "[data-private], .ph-mask, .ph-no-capture",
      blockSelector: "[data-ph-no-capture]",
      recordCrossOriginIframes: false,
    },
    capture_heatmaps: true,

    loaded: (ph) => {
      if (process.env.NODE_ENV === "development") ph.debug();
    },
  });

  initialized = true;
}

function getBrowserContextProperties(): PostHogEventProperties {
  if (typeof window === "undefined") return {};

  const url = new URL(window.location.href);

  return {
    path: url.pathname,
    url: url.toString(),
    referrer: document.referrer || undefined,
    utm_source: url.searchParams.get("utm_source") || undefined,
    utm_medium: url.searchParams.get("utm_medium") || undefined,
    utm_campaign: url.searchParams.get("utm_campaign") || undefined,
    utm_content: url.searchParams.get("utm_content") || undefined,
    utm_term: url.searchParams.get("utm_term") || undefined,
  };
}

export function capturePostHogEvent(
  event: string,
  properties?: PostHogEventProperties,
): void {
  if (typeof window === "undefined") return;

  initPostHog();
  posthog.capture(event, {
    ...getBrowserContextProperties(),
    ...properties,
  });
}

export function identifyPostHogUser(
  userId: string,
  properties?: PostHogEventProperties,
): void {
  if (typeof window === "undefined") return;

  initPostHog();
  posthog.identify(userId, properties);
}

export function resetPostHogUser(): void {
  if (typeof window === "undefined") return;

  initPostHog();
  posthog.reset();
}

export { posthog };
